import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Space, Button, DatePicker, Select, Input,
  message, Typography, Empty, Tooltip, Row, Col, Divider
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, EyeOutlined,
  CalendarOutlined, UserOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { AppointmentDetailModal } from '../Appointments';
import { getTokens } from '../../services/authApi';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface CancelledAppointmentsViewProps {
  onBack?: () => void;
}

export const CancelledAppointmentsView: React.FC<CancelledAppointmentsViewProps> = ({ onBack }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [searchText, setSearchText] = useState('');
  const [filterTechnician, setFilterTechnician] = useState<number | undefined>();
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    loadTechnicians();
    loadCancelledAppointments();
  }, [dateRange]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadTechnicians = async () => {
    try {
      const tokens = getTokens();
      const headers: HeadersInit = tokens?.accessToken ? {
        'Authorization': `Bearer ${tokens.accessToken}`
      } : {};

      const response = await fetch('/api/users?role=technician&status=active', { headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Ensure we have an array
      if (Array.isArray(data)) {
        setTechnicians(data);
      } else {
        console.error('Technicians data is not an array:', data);
        setTechnicians([]);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
      setTechnicians([]); // Set to empty array on error
    }
  };

  const loadCancelledAppointments = async () => {
    setLoading(true);
    try {
      const data = await appointmentApi.getAll({
        start_date: dateRange[0].startOf('day').toISOString(),
        end_date: dateRange[1].endOf('day').toISOString(),
        status: 'cancelled'
      });
      setAppointments(data);
    } catch (error) {
      console.error('Error loading cancelled appointments:', error);
      message.error('Failed to load cancelled appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadCancelledAppointments();
    message.success('Cancelled appointments refreshed');
  };

  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setDetailModalVisible(true);
  };

  // Filter appointments based on search and technician filter
  const getFilteredAppointments = () => {
    let filtered = appointments;

    // Apply search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(apt =>
        apt.title.toLowerCase().includes(search) ||
        apt.customer?.first_name?.toLowerCase().includes(search) ||
        apt.customer?.last_name?.toLowerCase().includes(search) ||
        apt.vehicle?.make?.toLowerCase().includes(search) ||
        apt.vehicle?.model?.toLowerCase().includes(search)
      );
    }

    // Apply technician filter
    if (filterTechnician) {
      filtered = filtered.filter(apt => apt.technician_id === filterTechnician);
    }

    return filtered;
  };

  const renderMobileCards = () => {
    const filtered = getFilteredAppointments();

    if (filtered.length === 0) {
      return (
        <Empty
          description="No cancelled appointments found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {filtered.map((appointment) => (
          <Card
            key={appointment.id}
            size="small"
            style={{ borderLeft: '4px solid #ff4d4f' }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {/* Cancelled Date */}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Cancelled</Text>
                <br />
                <Space size="small">
                  <CalendarOutlined style={{ color: '#ff4d4f' }} />
                  <Text strong style={{ fontSize: 14 }}>
                    {dayjs(appointment.cancelled_at).format('MMM DD, YYYY')}
                  </Text>
                </Space>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(appointment.cancelled_at).format('h:mm A')}
                </Text>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* Appointment Info */}
              <div>
                <Text strong style={{ fontSize: 15 }}>{appointment.title}</Text>
                <br />
                <Tag color="blue" style={{ marginTop: 4 }}>
                  {appointment.appointment_type?.toUpperCase()}
                </Tag>
              </div>

              {/* Original Date */}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Original Date</Text>
                <br />
                <Space size="small">
                  <ClockCircleOutlined />
                  <Text style={{ fontSize: 14 }}>
                    {dayjs(appointment.start_time).format('MMM DD, YYYY @ h:mm A')}
                  </Text>
                </Space>
              </div>

              {/* Customer */}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Customer</Text>
                <br />
                <Space size="small">
                  <UserOutlined />
                  <div>
                    <Text style={{ fontSize: 14 }}>
                      {appointment.customer?.company_name ||
                       `${appointment.customer?.first_name} ${appointment.customer?.last_name}`}
                    </Text>
                    {appointment.vehicle && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {appointment.vehicle.year} {appointment.vehicle.make} {appointment.vehicle.model}
                        </Text>
                      </>
                    )}
                  </div>
                </Space>
              </div>

              {/* Technician */}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Technician</Text>
                <br />
                <Text style={{ fontSize: 14 }}>{appointment.technician_name || 'Unknown'}</Text>
              </div>

              {/* Cancellation Reason */}
              {appointment.cancellation_reason && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Reason</Text>
                  <br />
                  <Text style={{ fontSize: 13 }}>{appointment.cancellation_reason}</Text>
                </div>
              )}

              {/* View Button */}
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetails(appointment)}
                block
                style={{ marginTop: 8 }}
              >
                View Details
              </Button>
            </Space>
          </Card>
        ))}
      </Space>
    );
  };

  const columns: ColumnsType<Appointment> = [
    {
      title: 'Cancelled Date',
      dataIndex: 'cancelled_at',
      key: 'cancelled_at',
      width: 180,
      render: (cancelled_at) => (
        <Space>
          <CalendarOutlined />
          <Text>{dayjs(cancelled_at).format('MMM DD, YYYY @ h:mm A')}</Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.cancelled_at).unix() - dayjs(b.cancelled_at).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Original Date',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (start_time) => (
        <Space>
          <ClockCircleOutlined />
          <Text>{dayjs(start_time).format('MMM DD, YYYY @ h:mm A')}</Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.start_time).unix() - dayjs(b.start_time).unix()
    },
    {
      title: 'Appointment',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <div>
          <Text strong>{title}</Text>
          <br />
          <Tag color="blue">{record.appointment_type?.toUpperCase()}</Tag>
        </div>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) => {
        const searchValue = value.toString().toLowerCase();
        return (
          record.title.toLowerCase().includes(searchValue) ||
          record.customer?.first_name?.toLowerCase().includes(searchValue) ||
          record.customer?.last_name?.toLowerCase().includes(searchValue) ||
          record.vehicle?.make?.toLowerCase().includes(searchValue) ||
          record.vehicle?.model?.toLowerCase().includes(searchValue)
        );
      }
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            {record.customer?.company_name ||
             `${record.customer?.first_name} ${record.customer?.last_name}`}
          </Text>
          {record.vehicle && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.vehicle.year} {record.vehicle.make} {record.vehicle.model}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Technician',
      key: 'technician',
      width: 150,
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <Text>{record.technician_name || 'Unknown'}</Text>
        </Space>
      ),
      filteredValue: filterTechnician ? [filterTechnician.toString()] : null,
      onFilter: (value, record) => record.technician_id === parseInt(value.toString())
    },
    {
      title: 'Cancellation Reason',
      dataIndex: 'cancellation_reason',
      key: 'cancellation_reason',
      ellipsis: {
        showTitle: false,
      },
      render: (reason) => (
        <Tooltip placement="topLeft" title={reason}>
          <Text type="secondary">{reason || 'No reason provided'}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card>
        {/* Header */}
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <Row gutter={[8, 8]} align="middle" justify="space-between">
            <Col xs={24} sm={16}>
              <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                Cancelled Appointments
              </Title>
              {!isMobile && (
                <Text type="secondary">
                  View historical cancelled appointment records
                </Text>
              )}
            </Col>
            <Col xs={24} sm={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Space wrap size={isMobile ? 'small' : 'middle'}>
                {onBack && (
                  <Button onClick={onBack} size={isMobile ? 'small' : 'middle'}>
                    Back to Schedule
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  size={isMobile ? 'small' : 'middle'}
                >
                  {isMobile ? '' : 'Refresh'}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: 16 }}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small" style={{ width: '100%' }}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="MMM DD, YYYY"
              style={{ width: isMobile ? '100%' : 'auto' }}
              size={isMobile ? 'middle' : 'middle'}
            />
            <Select
              placeholder="Filter by Technician"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              value={filterTechnician}
              onChange={setFilterTechnician}
              size={isMobile ? 'middle' : 'middle'}
            >
              {technicians.map(tech => (
                <Option key={tech.id} value={tech.id}>
                  {tech.first_name} {tech.last_name}
                </Option>
              ))}
            </Select>
            <Input
              placeholder="Search appointments..."
              prefix={<SearchOutlined />}
              style={{ width: isMobile ? '100%' : 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size={isMobile ? 'middle' : 'middle'}
            />
          </Space>
        </div>

        {/* Content - Cards for mobile, Table for desktop */}
        {isMobile ? (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Empty description="Loading..." />
              </div>
            ) : (
              renderMobileCards()
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={appointments}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} cancelled appointments`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            locale={{
              emptyText: (
                <Empty
                  description="No cancelled appointments found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>

      {/* Detail Modal */}
      <AppointmentDetailModal
        visible={detailModalVisible}
        appointment={selectedAppointment}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedAppointment(null);
        }}
      />
    </div>
  );
};
